import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Modal, ModalVariant } from '@/components';
import { TextField } from '@/components/TextField';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { getFileExtension, stripFileExtension } from '@/utils/file-name';

interface EditSourceModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialName: string;
    lockExtension?: boolean;
    onSubmit: (name: string) => Promise<void>;
}

interface EditSourceFormValues {
    basename: string;
}

export function EditSourceModal({
    isOpen,
    onClose,
    initialName,
    lockExtension = false,
    onSubmit,
}: EditSourceModalProps) {
    const { t } = useTranslation(I18nNamespace.SOURCES);

    const lockedExtension = lockExtension ? getFileExtension(initialName) : '';
    const initialBasename = lockedExtension
        ? stripFileExtension(initialName)
        : initialName;

    const methods = useForm<EditSourceFormValues>({
        defaultValues: { basename: '' },
    });

    const {
        register,
        handleSubmit,
        reset,
        watch,
        setFocus,
        formState: { isSubmitting },
    } = methods;

    const basenameValue = watch('basename');
    const trimmedCurrentBasename = basenameValue?.trim() ?? '';
    const trimmedInitialBasename = initialBasename.trim();

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        reset({ basename: initialBasename });
    }, [isOpen, initialBasename, reset]);

    const handleOpenAutoFocus = (event: Event) => {
        event.preventDefault();
        setFocus('basename');
        const input = document.activeElement;
        if (input instanceof HTMLInputElement) {
            const length = input.value.length;
            input.setSelectionRange(length, length);
        }
    };

    const handleClose = () => {
        if (isSubmitting) {
            return;
        }
        reset({ basename: '' });
        onClose();
    };

    const onValidSubmit = async (values: EditSourceFormValues) => {
        const trimmedBasename = values.basename.trim();
        await onSubmit(`${trimmedBasename}${lockedExtension}`);
        reset({ basename: '' });
        onClose();
    };

    return (
        <FormProvider {...methods}>
            <Modal
                isOpen={isOpen}
                onClose={handleClose}
                title={t('editModal.title')}
                variant={ModalVariant.PLAIN}
                onCancel={handleClose}
                onSubmit={() => void handleSubmit(onValidSubmit)()}
                submitText={t('editModal.submit')}
                submitDisabled={
                    !trimmedCurrentBasename ||
                    trimmedCurrentBasename === trimmedInitialBasename ||
                    isSubmitting
                }
                submitInProgress={isSubmitting}
                size="medium"
                closeOnOutsideClick
                onOpenAutoFocus={handleOpenAutoFocus}
            >
                <div className="flex flex-col gap-6 p-6">
                    <TextField
                        label={t('editModal.nameLabel')}
                        {...register('basename')}
                        required
                    />
                </div>
            </Modal>
        </FormProvider>
    );
}
